import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  changeReadOnly, enableDisable, selectWorkspace, updateWS, workspaceSelectedFromTable
} from 'Screens/Contextualization/Workspaces/actions/Actions';
import TimeAgo from 'react-timeago';
import get from 'lodash/get';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import {
  Wrapper, Title, Label, Text, FieldContainer, LockIcon, UnlockIcon,
  MoreActionsWrapper, MoreButton, MoreIcon, Dropdown, DropdownItem, DropdownText,
  ArchiveIcon, UnarchiveIcon
} from './styled';
import Stats from '../Stats';
import Histogram from '../Histogram';
import Importance from 'Common/Components/Importance';
import useClickOutside from 'Hooks/useClickOutside';

const WorkspaceItem = ({
  ws, selected, setShowConfirmation
}) => {
  const dispatch = useDispatch();
  const updateDate = get(ws, 'update_date', null);
  const lastRunAgent = get(ws, 'last_run_agent_date', null);
  const [showDropdown, toggleDropdown] = useState(false);

  const handleSelectWorkspace = () => {
    dispatch(selectWorkspace(selected ? [] : [ws]));
  };

  const handleSelect = () => (ws.active) && dispatch(workspaceSelectedFromTable(ws));

  const toggleActive = (e) => {
    e.stopPropagation();
    if (ws.active) {
      setShowConfirmation({ show: true, ws: ws.name });
    } else {
      dispatch(enableDisable(ws.name, false));
    }
    toggleDropdown(false);
  };

  const toggleMoreActions = (e) => {
    e.stopPropagation();
    toggleDropdown(!showDropdown);
  };

  const toggleReadOnly = (e) => {
    e.stopPropagation();
    dispatch(changeReadOnly(ws.name, !ws.readonly));
    toggleDropdown(false);
  };

  const parseDate = (d) => {
    const today = moment();
    const isToday = moment(d).isSame(today, 'day');
    if (isToday) return 'Today';
    return moment(d).format('ddd D MMM YYYY');
  };

  const parsedDateRange = (ws) => {
    const startDate = get(ws, 'duration.start_date', null);
    const endDate = get(ws, 'duration.end_date', null);
    if (!startDate || !endDate) return 'No start or end date defined.';
    return `${parseDate(startDate)} - ${parseDate(endDate)}`;
  };

  const dropdownRef = useRef();
  useClickOutside(dropdownRef, () => toggleDropdown(false));

  return (
    <Wrapper selected={ selected } active={ ws.active } onClick={ handleSelectWorkspace } title={ ws.active ? null : 'This Workspace is archived' }>
      <FieldContainer>
        <Importance importance={ ws.importance } callback={ (importance) => dispatch(updateWS(ws.name, { importance })) } />
        <Title onClick={ handleSelect }>{ ws.name }</Title>
        { ws.readonly ? <LockIcon title="This Workspace is locked (read-only)" /> : <UnlockIcon title="This Workspace is unlocked" />}
        <MoreActionsWrapper ref={ dropdownRef }>
          <MoreButton onClick={ toggleMoreActions } title="More"><MoreIcon title="More" /></MoreButton>
          { showDropdown &&
            <Dropdown>
              { ws.readonly
                ? <DropdownItem onClick={ toggleReadOnly }>
                    <UnlockIcon />
                    <DropdownText>Unlock</DropdownText>
                  </DropdownItem>
                : <DropdownItem onClick={ toggleReadOnly }>
                    <LockIcon />
                    <DropdownText>Lock</DropdownText>
                  </DropdownItem>
              }
              { ws.active
                ? <DropdownItem onClick={ toggleActive }>
                    <ArchiveIcon />
                    <DropdownText>Archive</DropdownText>
                  </DropdownItem>
                : <DropdownItem onClick={ toggleActive }>
                    <UnarchiveIcon />
                    <DropdownText>Unarchive</DropdownText>
                  </DropdownItem>
              }
            </Dropdown>
          }
        </MoreActionsWrapper>
      </FieldContainer>
      <FieldContainer>
        <Text>{ parsedDateRange(ws) }</Text>
      </FieldContainer>
      <FieldContainer>
        <Label>Description:</Label>
        <Text>{ ws.description }</Text>
      </FieldContainer>
      <Stats totalVulns={ ws.stats.total_vulns } totalAssets={ ws.stats.hosts } totalServices={ ws.stats.services } totalConfirmed={ ws.stats.confirmed_vulns } totalCritical={ ws.stats.critical_vulns } totalOpen={ ws.stats.opened_vulns } />
      <FieldContainer>
        <Label>Last Modified:</Label>
        <Text>
          { updateDate ? <TimeAgo date={ updateDate } /> : <div children="Not available." /> }
        </Text>
      </FieldContainer>
      <FieldContainer>
        <Label>Last Agent Execution:</Label>
        <Text>
          { lastRunAgent ? <TimeAgo date={ lastRunAgent } /> : <div children="Never." /> }
        </Text>
      </FieldContainer>
      <Histogram data={ ws.histogram } />
    </Wrapper>
  );
};

WorkspaceItem.propTypes = {
  ws: PropTypes.instanceOf(Object).isRequired,
  selected: PropTypes.bool.isRequired
};

export default WorkspaceItem;
